import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import apiService, { type Invitation } from '../services/api';
import { usePageTitle } from '../hooks/usePageTitle';
import toast from 'react-hot-toast';
import {
  UserPlusIcon,
  XMarkIcon,
  PaperAirplaneIcon,
  BuildingOfficeIcon,
  ClipboardDocumentIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';

type ViewMode = 'user' | 'admin' | 'sysadmin';
function getEffectiveMode(viewMode: ViewMode, role: string | undefined): ViewMode {
  if (role === 'sysadmin') return viewMode;
  if (role === 'admin') return viewMode === 'sysadmin' ? 'admin' : viewMode;
  return 'user';
}

/** Min 2 chars after last dot so typing "x@y.z" one char at a time doesn't flip validation each keystroke */
const EMAIL_STABLE_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
function isValidEmailStable(value: string): boolean {
  return EMAIL_STABLE_REGEX.test((value ?? '').trim());
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const h = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  const s = String(d.getSeconds()).padStart(2, '0');
  return `${y}-${m}-${day} ${h}:${min}:${s}`;
}

function emailDomainInAllowed(email: string, domains: string[]): boolean {
  const part = email.trim().split('@')[1]?.toLowerCase();
  if (!part) return false;
  return domains.some((d) => (d || '').trim().toLowerCase() === part);
}

const Invitations: React.FC = () => {
  const { t } = useTranslation('invitations');
  const { user } = useAuth();
  const queryClient = useQueryClient();
  usePageTitle(t('title'));

  const isSysadmin = user?.role === 'sysadmin';
  const isAdmin = user?.role === 'admin' || user?.role === 'sysadmin';

  const [viewMode, setViewMode] = useState<ViewMode>(() => (localStorage.getItem('view_mode') as ViewMode) || 'user');
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.viewMode) setViewMode(detail.viewMode);
    };
    window.addEventListener('view-mode-changed', handler);
    return () => window.removeEventListener('view-mode-changed', handler);
  }, []);
  const effectiveMode = getEffectiveMode(viewMode, user?.role);

  // Sysadmin in standard (user) mode: behave like regular user — no company selector, reader-only invites
  const actAsRegularUser = isSysadmin && effectiveMode === 'user';
  // Company selector only in sysadmin view; in admin view sysadmin sees their own company like a regular company admin
  const showCompanySelector = isSysadmin && effectiveMode === 'sysadmin';

  const companyId = showCompanySelector ? undefined : user?.company_id;
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(companyId ?? null);
  const effectiveCompanyId = actAsRegularUser
    ? (user?.company_id ?? null)
    : showCompanySelector
      ? selectedCompanyId
      : (user?.company_id ?? null);

  const { data: companies } = useQuery({
    queryKey: ['companies'],
    queryFn: () => apiService.getCompanies(),
    enabled: isSysadmin,
  });

  // Same source of truth as Usage page: same endpoint and same query key (default filters) so cache is shared
  const { data: companyUsages } = useQuery({
    queryKey: ['company-usages', effectiveCompanyId, 'active', 'all'],
    queryFn: () => apiService.getCompanyUsages(effectiveCompanyId!, { status: 'active', type: 'all' }),
    enabled: !!effectiveCompanyId,
  });

  const { data: company } = useQuery({
    queryKey: ['company', effectiveCompanyId],
    queryFn: () => apiService.getCompany(effectiveCompanyId!),
    enabled: !!effectiveCompanyId,
  });
  const allowedEmailDomains: string[] = (() => {
    const s = company?.settings;
    if (!s) return [];
    try {
      const parsed = typeof s === 'string' ? JSON.parse(s) : s;
      const arr = parsed?.allowed_email_domains;
      return Array.isArray(arr) ? arr : [];
    } catch {
      return [];
    }
  })();

  // Same logic as Usage page: used < max for each type (no reader-pool subtraction)
  const fullUsed = companyUsages?.full_licenses_used ?? 0;
  const fullMax = companyUsages?.full_licenses_max ?? 0;
  const readerUsed = companyUsages?.reader_licenses_used ?? 0;
  const readerMax = companyUsages?.reader_licenses_max ?? 0;
  const auditorUsed = companyUsages?.auditor_licenses_used ?? 0;
  const auditorMax = companyUsages?.auditor_licenses_max ?? null;

  const canAddFullUser = fullUsed < fullMax;
  const canAddReaderUser = readerUsed < readerMax;
  const canAddAuditor = auditorMax == null || auditorUsed < auditorMax;
  const canSelectReader = canAddReaderUser;

  // Sysadmin in standard mode: same as regular user — reader-only
  const isAdminForInviteForm = isAdmin && !actAsRegularUser;

  const allLicensesMaxed =
    !!companyUsages &&
    fullUsed >= fullMax &&
    readerUsed >= readerMax &&
    (auditorMax != null && auditorUsed >= auditorMax);

  const canInviteAny =
    !!companyUsages &&
    !allLicensesMaxed &&
    (isAdminForInviteForm
      ? canAddFullUser || canAddReaderUser || canAddAuditor
      : canAddReaderUser);
  // User role (full/reader) only available when at least one of full or reader has capacity
  const canAddUserOrReader = canAddFullUser || canAddReaderUser;

  const { data: invitationsMine, isLoading: loadingMine } = useQuery({
    queryKey: ['invitations', 'mine'],
    queryFn: () => apiService.getInvitationsMine(),
    enabled: !!user,
  });

  // Everyone sees only invitations they sent (not invitations they received)
  const invitationsData = invitationsMine;
  const isLoading = loadingMine;

  const createMutation = useMutation({
    mutationFn: (body: { invitee_email: string; role?: string; mode?: string }) =>
      apiService.createInvitation(effectiveCompanyId!, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
      toast.success(t('toastCreated'));
      setInviteEmail('');
      setInviteRole('user');
      setInviteMode('full');
      setShowInviteForm(false);
    },
    onError: async (err: { response?: { data?: { error?: string; code?: string; invitation_id?: string } }; message?: string }) => {
      const data = err.response?.data;
      const msg = data?.error || err.message || '';
      if (data?.code === 'pending_invite_same_inviter' && data?.invitation_id) {
        try {
          const inv = await apiService.getInvitationById(data.invitation_id);
          setEditInvitation(inv as Invitation);
          setEditRole((inviteRole || 'user') as 'user' | 'admin');
          setEditMode((inviteMode || 'full') as 'full' | 'reader');
          toast(t('toastAlreadySentUpdateBelow'), { icon: 'ℹ️' });
        } catch {
          toast.error(msg || t('toastCreateFailed'));
        }
        return;
      }
      if (msg.toLowerCase().includes('already exists') && msg.toLowerCase().includes('email')) {
        toast.error(t('errorUserAlreadyExists'));
        return;
      }
      if (msg.toLowerCase().includes('someone else')) {
        toast.error(t('errorSomeoneElseInvited'));
        return;
      }
      toast.error(msg || t('toastCreateFailed'));
    },
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) => apiService.cancelInvitation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
      toast.success(t('toastCancelled'));
    },
  });

  const resendMutation = useMutation({
    mutationFn: (id: string) => apiService.resendInvitation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
      toast.success(t('toastResent'));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, role, mode }: { id: string; role: string; mode: string }) =>
      apiService.updateInvitation(id, { role, mode }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
      toast.success(t('toastInvitationUpdated'));
      setEditInvitation(null);
    },
    onError: (err: { response?: { data?: { error?: string } }; message?: string }) => {
      toast.error(err.response?.data?.error || err.message || t('toastUpdateFailed'));
    },
  });

  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'user' | 'admin' | 'company_auditor' | 'system_auditor'>('user');
  const [inviteMode, setInviteMode] = useState<'full' | 'reader'>('full');
  const [statusFilter, setStatusFilter] = useState<'pending' | 'used' | 'cancelled' | 'all'>('all');
  const [roleFilter, setRoleFilter] = useState<'all' | 'user' | 'admin' | 'company_auditor' | 'system_auditor'>('all');
  const [modeFilter, setModeFilter] = useState<'all' | 'full' | 'reader'>('all');
  const [sortBy, setSortBy] = useState<'sent' | 'expiry'>('sent');
  const [editInvitation, setEditInvitation] = useState<Invitation | null>(null);
  const [editRole, setEditRole] = useState<'user' | 'admin'>('user');
  const [editMode, setEditMode] = useState<'full' | 'reader'>('reader');
  const isAuditorRole = inviteRole === 'company_auditor' || inviteRole === 'system_auditor';

  const defaultsSetForFormOpenRef = useRef(false);
  useEffect(() => {
    if (!showInviteForm) {
      defaultsSetForFormOpenRef.current = false;
      return;
    }
    if (!isAdminForInviteForm || !companyUsages) return;
    if (defaultsSetForFormOpenRef.current) return;
    defaultsSetForFormOpenRef.current = true;
    const defaultRole: typeof inviteRole =
      canAddUserOrReader ? 'user'
      : canAddFullUser ? 'admin'
      : canAddAuditor ? 'company_auditor'
      : isSysadmin ? 'system_auditor'
      : 'user';
    const defaultMode: 'full' | 'reader' =
      canAddFullUser ? 'full' : canAddReaderUser ? 'reader' : 'full';
    setInviteRole(defaultRole);
    setInviteMode(defaultMode);
  }, [
    showInviteForm,
    isAdminForInviteForm,
    companyUsages,
    canAddUserOrReader,
    canAddFullUser,
    canAddReaderUser,
    canAddAuditor,
    isSysadmin,
  ]);

  const invitations: Invitation[] = invitationsData?.invitations ?? [];
  const filteredInvitations = invitations.filter((inv) => {
    if (statusFilter !== 'all') {
      if (statusFilter === 'pending' && inv.status !== 'pending') return false;
      if (statusFilter === 'used' && inv.status !== 'used') return false;
      if (statusFilter === 'cancelled' && inv.status !== 'cancelled' && inv.status !== 'expired') return false;
    }
    if (roleFilter !== 'all' && inv.role !== roleFilter) return false;
    if (modeFilter !== 'all' && inv.mode !== modeFilter) return false;
    return true;
  });

  const sortedInvitations = [...filteredInvitations].sort((a, b) => {
    if (sortBy === 'expiry') {
      return new Date(a.expires_at).getTime() - new Date(b.expires_at).getTime();
    }
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  const hasListFilters = statusFilter !== 'all' || roleFilter !== 'all' || modeFilter !== 'all';
  const showList = isLoading || invitations.length > 0 || hasListFilters;

  if (!user) return null;

  return (
    <div className="p-4 sm:p-6 max-w-4xl">
      {showCompanySelector && companies && companies.length > 0 && (
        <div className="mb-6 flex items-center gap-2">
          <BuildingOfficeIcon className="w-5 h-5 text-gray-500" />
          <select
            value={selectedCompanyId ?? ''}
            onChange={(e) => setSelectedCompanyId(e.target.value || null)}
            className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 text-sm"
          >
            <option value="">{t('selectCompany')}</option>
            {companies.map((c: { id: string; name: string }) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {!effectiveCompanyId && (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-6 text-center mb-6">
          <p className="text-gray-600 dark:text-gray-400">
            {showCompanySelector ? t('noCompanySysadmin') : t('noCompanyUser')}
          </p>
        </div>
      )}

      {effectiveCompanyId && (
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <button
            type="button"
            disabled={!!companyUsages && !canInviteAny}
            onClick={() => canInviteAny && setShowInviteForm((v) => !v)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-indigo-600 font-medium transition-colors"
          >
            <UserPlusIcon className="w-5 h-5" />
            {t('inviteUser')}
          </button>
          {!!companyUsages && !canInviteAny && (
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {isAdminForInviteForm
                ? `${t('noLicensesAvailable')}. ${t('noLicensesAvailableNote')}`
                : t('noReaderLicensesAvailable')}
            </span>
          )}
        </div>
      )}

      {(effectiveCompanyId || !isAdminForInviteForm) && (
        <>

          {showInviteForm && canInviteAny && (
            <form
              className="mb-6 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                const email = inviteEmail.trim();
                if (!email) {
                  toast.error(t('toastEmailRequired'));
                  return;
                }
                if (!isValidEmailStable(email)) {
                  toast.error(t('toastInvalidEmail'));
                  return;
                }
                if ((user?.email ?? '').trim().toLowerCase() === email.toLowerCase()) {
                  toast.error(t('cannotInviteYourself'));
                  return;
                }
                const outsideDomain =
                  isAdminForInviteForm &&
                  !isSysadmin &&
                  allowedEmailDomains.length > 0 &&
                  !emailDomainInAllowed(email, allowedEmailDomains);
                if (outsideDomain && (inviteRole === 'company_auditor' || inviteRole === 'system_auditor')) {
                  toast.error('Company auditors must have an email address matching the company domain.');
                  return;
                }
                if (outsideDomain && inviteMode === 'full') {
                  toast.error(`Only reader invites are allowed for users outside the company domain. Please select Reader mode.`);
                  return;
                }
                const readerQuotaExhausted =
                  isAdminForInviteForm &&
                  inviteMode === 'reader' &&
                  !isAuditorRole &&
                  !canAddReaderUser;
                if (readerQuotaExhausted) {
                  toast.error(t('readerInvitesRanOut'));
                  return;
                }
                createMutation.mutate({
                  invitee_email: email,
                  role: isAdminForInviteForm ? inviteRole : 'user',
                  mode: isAdminForInviteForm ? (isAuditorRole ? 'reader' : inviteMode) : 'reader',
                });
              }}
            >
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">{t('newInvitation')}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {isAdminForInviteForm && (
                  <>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('role')}</label>
                      <select
                        value={inviteRole}
                        onChange={(e) => {
                          const v = e.target.value as typeof inviteRole;
                          if (v === 'user' && !canAddUserOrReader) return;
                          if (v === 'admin' && !canAddFullUser) return;
                          if (v === 'company_auditor' && !canAddAuditor) return;
                          setInviteRole(v);
                        }}
                        className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 text-sm disabled:opacity-50"
                      >
                        <option value="user" disabled={!canAddUserOrReader}>
                          {t('userRole')}
                        </option>
                        <option value="admin" disabled={!canAddFullUser}>
                          {t('adminRole')}
                        </option>
                        <option value="company_auditor" disabled={!canAddAuditor}>
                          Company Auditor
                        </option>
                        {isSysadmin && (
                          <option value="system_auditor">System Auditor</option>
                        )}
                      </select>
                    </div>
                    {!isAuditorRole && (
                    <div>
                      <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('mode')}</label>
                      <select
                        value={inviteMode}
                        onChange={(e) => {
                          const v = e.target.value as 'full' | 'reader';
                          if (v === 'full' && !canAddFullUser) return;
                          if (v === 'reader' && !canAddReaderUser) return;
                          setInviteMode(v);
                        }}
                        className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 text-sm disabled:opacity-50"
                      >
                        <option value="full" disabled={!canAddFullUser}>
                          {t('fullMode')}
                        </option>
                        <option value="reader" disabled={!canAddReaderUser}>
                          {t('readerMode')}
                        </option>
                      </select>
                    </div>
                    )}
                  </>
                )}
                <div className={isAdminForInviteForm ? 'sm:col-span-2' : ''}>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('email')}</label>
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder={t('emailPlaceholder')}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-2 text-sm"
                    required
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={createMutation.isPending || (inviteRole === 'company_auditor' && !canAddAuditor)}
                  className="inline-flex items-center gap-1 px-3 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 text-sm font-medium"
                >
                  <PaperAirplaneIcon className="w-4 h-4" />
                  {t('sendInvitation')}
                </button>
                <button
                  type="button"
                  onClick={() => setShowInviteForm(false)}
                  className="inline-flex items-center gap-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 text-sm"
                >
                  {t('cancel')}
                </button>
              </div>
            </form>
          )}

          {showList && <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-gray-900 dark:text-white">{t('sortBy')}</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as 'sent' | 'expiry')}
                  className="min-w-[160px] rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-1.5 text-sm"
                  aria-label={t('sortBy')}
                >
                  <option value="sent">{t('sortBySent')}</option>
                  <option value="expiry">{t('sortByExpiry')}</option>
                </select>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value as 'pending' | 'used' | 'cancelled' | 'all')}
                  className="min-w-[240px] rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-1.5 text-sm"
                  aria-label={t('filterByStatus')}
                >
                  <option value="pending">{t('pendingInvitations')}</option>
                  <option value="used">{t('usedInvitations')}</option>
                  <option value="cancelled">{t('cancelledInvitations')}</option>
                  <option value="all">{t('allInvitations')}</option>
                </select>
                {isAdminForInviteForm && (
                  <>
                    <select
                      value={roleFilter}
                      onChange={(e) => setRoleFilter(e.target.value as typeof roleFilter)}
                      className="min-w-[140px] rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-1.5 text-sm"
                      aria-label={t('filterByRole')}
                    >
                      <option value="all">{t('allRoles')}</option>
                      <option value="user">{t('userRole')}</option>
                      <option value="admin">{t('adminRole')}</option>
                      <option value="company_auditor">Company Auditor</option>
                      <option value="system_auditor">System Auditor</option>
                    </select>
                    <select
                      value={modeFilter}
                      onChange={(e) => setModeFilter(e.target.value as 'all' | 'full' | 'reader')}
                      className="min-w-[140px] rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-3 py-1.5 text-sm"
                      aria-label={t('filterByType')}
                    >
                      <option value="all">{t('allTypes')}</option>
                      <option value="full">{t('fullMode')}</option>
                      <option value="reader">{t('readerMode')}</option>
                    </select>
                  </>
                )}
              </div>
            </div>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white" />
              </div>
            ) : sortedInvitations.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                {isAdminForInviteForm ? t('emptyAdmin') : t('emptyUser')}
              </div>
            ) : (
              <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                {sortedInvitations.map((inv) => (
                  <li key={inv.id} className="px-4 py-3 flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{inv.invitee_email}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {isAdminForInviteForm
                          ? `${inv.role === 'admin' ? t('adminRole') : inv.role === 'company_auditor' ? 'Company Auditor' : inv.role === 'system_auditor' ? 'System Auditor' : t('userRole')}${inv.role !== 'company_auditor' && inv.role !== 'system_auditor' ? ` / ${inv.mode === 'full' ? t('fullMode') : t('readerMode')}`: ''} · ${inv.status} · ${t('sent')} ${formatDateTime(inv.created_at)} · ${t('expires')} ${formatDateTime(inv.expires_at)}`
                          : `${inv.status} · ${t('sent')} ${formatDateTime(inv.created_at)} · ${t('expires')} ${formatDateTime(inv.expires_at)}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {inv.status === 'pending' && (
                        <>
                          {isAdminForInviteForm && (
                            <button
                              type="button"
                              onClick={() => {
                                setEditInvitation(inv);
                                setEditRole((inv.role === 'admin' ? 'admin' : 'user') as 'user' | 'admin');
                                setEditMode((inv.mode === 'full' ? 'full' : 'reader') as 'full' | 'reader');
                              }}
                              className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-amber-600 dark:hover:text-amber-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
                              title={t('updateInvitation')}
                            >
                              <PencilSquareIcon className="w-5 h-5" />
                            </button>
                          )}
                          {inv.token && (
                            <button
                              type="button"
                              onClick={() => {
                                const cleanToken = String(inv.token).trim().replace(/[^a-fA-F0-9]/g, '');
                                const link = `${window.location.origin}/accept-invite?token=${cleanToken}`;
                                navigator.clipboard.writeText(link).then(
                                  () => toast.success(t('copyLinkSuccess')),
                                  () => toast.error(t('copyLinkFailed'))
                                );
                              }}
                              className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
                              title={t('copyInviteLink')}
                            >
                              <ClipboardDocumentIcon className="w-5 h-5" />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => resendMutation.mutate(inv.id)}
                            disabled={resendMutation.isPending}
                            className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline disabled:opacity-50"
                          >
                            {t('resend')}
                          </button>
                          <button
                            type="button"
                            onClick={() => cancelMutation.mutate(inv.id)}
                            disabled={cancelMutation.isPending}
                            className="p-1 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                            title={t('cancelInvitation')}
                          >
                            <XMarkIcon className="w-5 h-5" />
                          </button>
                        </>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>}

          {editInvitation && (() => {
            const inviteeInCompanyDomain = emailDomainInAllowed(editInvitation.invitee_email, allowedEmailDomains);
            const readerOptionDisabled = inviteeInCompanyDomain && !canSelectReader;
            return (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
              role="dialog"
              aria-modal="true"
              aria-labelledby="edit-invitation-title"
              onClick={(e) => e.target === e.currentTarget && setEditInvitation(null)}
            >
              <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl max-w-md w-full border border-gray-200 dark:border-gray-700 overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                  <h3 id="edit-invitation-title" className="text-lg font-semibold text-gray-900 dark:text-white">{t('updateInvitation')}</h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{editInvitation.invitee_email}</p>
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{t('updateInvitationDescription')}</p>
                </div>
                <div className="px-5 py-4 space-y-5">
                  <fieldset className="space-y-2">
                    <legend className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('role')}</legend>
                    <div className="flex gap-6">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="edit-role"
                          checked={editRole === 'user'}
                          onChange={() => setEditRole('user')}
                          className="rounded-full border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="text-sm text-gray-900 dark:text-white">{t('userRole')}</span>
                      </label>
                      <label className={`flex items-center gap-2 ${!canAddFullUser ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
                        <input
                          type="radio"
                          name="edit-role"
                          checked={editRole === 'admin'}
                          onChange={() => canAddFullUser && setEditRole('admin')}
                          disabled={!canAddFullUser}
                          className="rounded-full border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 disabled:opacity-50"
                        />
                        <span className="text-sm text-gray-900 dark:text-white">{t('adminRole')}</span>
                      </label>
                    </div>
                    {!canAddFullUser && (
                      <p className="text-xs text-amber-600 dark:text-amber-400">{t('noFullLicenses')}</p>
                    )}
                  </fieldset>
                  <fieldset className="space-y-2">
                    <legend className="text-xs font-medium text-gray-500 dark:text-gray-400">{t('mode')}</legend>
                    <div className="flex gap-6">
                      <label className={`flex items-center gap-2 ${!canAddFullUser ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
                        <input
                          type="radio"
                          name="edit-mode"
                          checked={editMode === 'full'}
                          onChange={() => canAddFullUser && setEditMode('full')}
                          disabled={!canAddFullUser}
                          className="rounded-full border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 disabled:opacity-50"
                        />
                        <span className="text-sm text-gray-900 dark:text-white">{t('fullMode')}</span>
                      </label>
                      <label className={`flex items-center gap-2 ${readerOptionDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
                        <input
                          type="radio"
                          name="edit-mode"
                          checked={editMode === 'reader'}
                          onChange={() => !readerOptionDisabled && setEditMode('reader')}
                          disabled={readerOptionDisabled}
                          className="rounded-full border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 disabled:opacity-50"
                        />
                        <span className="text-sm text-gray-900 dark:text-white">{t('readerMode')}</span>
                      </label>
                    </div>
                    {!canAddFullUser && (
                      <p className="text-xs text-amber-600 dark:text-amber-400">{t('noFullLicenses')}</p>
                    )}
                    {readerOptionDisabled && inviteeInCompanyDomain && (
                      <p className="text-xs text-amber-600 dark:text-amber-400">{t('noReaderLicenses')}</p>
                    )}
                  </fieldset>
                </div>
                <div className="px-5 py-4 flex justify-end gap-2 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                  <button
                    type="button"
                    onClick={() => setEditInvitation(null)}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 text-sm font-medium"
                  >
                    {t('cancel')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      updateMutation.mutate({ id: editInvitation.id, role: editRole, mode: editMode });
                    }}
                    disabled={updateMutation.isPending || (editRole === editInvitation.role && editMode === editInvitation.mode)}
                    className="px-3 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 text-sm font-medium"
                  >
                    {updateMutation.isPending ? t('saving') : t('saveChanges')}
                  </button>
                </div>
              </div>
            </div>
            );
          })()}
        </>
      )}
    </div>
  );
};

export default Invitations;
